import React, { useMemo } from 'react';
import { Box, Typography, Stack, Avatar, Divider } from '@mui/material';
import { ResponsiveContainer, BarChart, XAxis, YAxis, Tooltip, Bar, LabelList, Cell } from 'recharts';
import { InfoOutlined } from '@mui/icons-material';

// Fonction globale de couleur pour les scores
const obtenirCouleurScore = (score) => {
  if (score >= 130) return '#3b82f6'; // Bleu leader
  if (score >= 50) return '#f59e0b';  // Orange
  return '#ef4444';                   // Rouge faible
};

export default function PerformanceScoreCard({ data }) {
  const apiUrl = process.env.REACT_APP_API_URL;

  // Préparation des données (Top 5)
  const top5Data = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    return [...data]
      .sort((a, b) => (b.performance_score || 0) - (a.performance_score || 0))
      .slice(0, 5)
      .map(intervenant => ({
        name: `${intervenant.prenom || ''} ${intervenant.nom || ''}`.trim(),
        score: intervenant.performance_score || 0,
        photo: intervenant.photo || ''
      }));
  }, [data]);

  if (top5Data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={150}>
        <Typography variant="caption" color="textSecondary">Aucune donnée disponible</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      
      {/* Zone Graphique principale - Hauteur augmentée de 5% (180px) */}
      <Box sx={{ display: 'flex', width: '100%', height: 210 }}>
        
        {/* Colonne des miniatures Avatars avec un peu plus d'espace */}
        <Stack 
          spacing={0} 
          justifyContent="space-around" 
          sx={{ 
            width: 32, 
            height: '100%', 
            pt: '8px', 
            pb: '8px', 
            alignItems: 'center' 
          }}
        >
          {top5Data.map((item, index) => {
            const couleurUnique = obtenirCouleurScore(item.score);
            return (
              <Avatar
                key={`avatar-${index}`}
                src={item.photo ? `${apiUrl}/storage/workshop/${item.photo}` : undefined}
                alt={item.name}
                sx={{ 
                  width: 26, // Légèrement agrandi
                  height: 26, 
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  bgcolor: `${couleurUnique}15`,
                  color: couleurUnique,
                  border: `1px solid ${couleurUnique}`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                }}
              >
                {item.name ? item.name.charAt(0) : 'I'}
              </Avatar>
            );
          })}
        </Stack>

        {/* Le Graphique Recharts réajusté */}
        <Box sx={{ flexGrow: 1, height: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={top5Data} 
              layout="vertical" 
              margin={{ top: 8, right: 35, left: 14, bottom: 8 }} // Marges aérées de 5%
            >
              <XAxis type="number" hide />
              
              <YAxis 
                dataKey="name" 
                type="category" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 11.5, fill: '#374151', fontWeight: 600 }}
              />
              
              <Tooltip 
                cursor={{ fill: '#f9fafb' }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length > 0 && payload[0].payload) {
                    const info = payload[0].payload;
                    return (
                      <Box sx={{ bgcolor: '#fff', p: 1.2, borderRadius: 1.5, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#111827' }}>
                          {info.name || 'Intervenant'}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: obtenirCouleurScore(info.score || 0), mt: 0.3 }}>
                          Score : {info.score || 0} pts
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                }}
              />
              
              <Bar dataKey="score" radius={[0, 4, 4, 0]} barSize={11}>
                <LabelList 
                  dataKey="score" 
                  position="right" 
                  style={{ fill: '#111827', fontSize: 11.5, fontWeight: 700, paddingLeft: 8 }} 
                />
                {top5Data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={obtenirCouleurScore(entry.score)} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Ligne de séparation fine */}
      <Divider sx={{ borderColor: '#f3f4f6', my: 0.5 }} />

      {/* Note Explicative compacte sur 3 lignes avec plus d'espace de conteneur */}
      <Box sx={{ 
        display: 'flex', 
        gap: 1.2, 
        bgcolor: '#f8fafc', 
        p: 1.4, 
        borderRadius: 2, 
        border: '1px dashed #e2e8f0' 
      }}>
        <InfoOutlined sx={{ fontSize: 16, color: '#64748b', mt: 0.1 }} />
        <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.4, fontWeight: 500 }}>
          <strong>Note :</strong> Liste des 5 meilleurs profils intervenants.<br />
          Le score est calculé selon le volume global de vos réservations,<br />
          ainsi que le taux de remplissage des créneaux et le CA généré.
        </Typography>
      </Box>

    </Box>
  );
}
